/**
 * What a record carries besides itself: a bill of materials, documents, or both.
 *
 * PRESENCE FOR THE ARTEFACTS, A COUNT FOR THE FILES, and the difference is which question each
 * answers. "Docs · 3" invites a reader to believe a number about a composition the grid has no
 * business asserting. "4 files" answers what somebody scanning forty rows before an audit is
 * actually asking: how much evidence is on this record.
 *
 * THE OLD OBJECTION WAS TO A MAINTAINED COUNT — a number kept right by a transaction on every
 * write, with a backfill and a lifetime of drift ahead of it. It does not reach a count read LIVE
 * in the same query as the presence beside it: by this component's own principle, a count that is
 * read can no more have drifted than a presence that is read. Asked for by Ed, 2026-10-21.
 *
 * THE COUNT INCLUDES FORM FIELDS. A File field's answer is an attachment like any other, and a
 * record whose form carries four certificates must not read as "1". See record_artefacts, where
 * the absence of a FIELD_CODE predicate is what makes that true.
 *
 * NOTHING AT ALL WHERE THERE IS NEITHER. Most records in a quality system are SOPs, forms and
 * training records that will never carry either. A column that draws something on every row is one
 * people stop reading, and an empty cell is the honest rendering of "this record has neither".
 *
 * THE FILES CHIP ASKS ABOUT THEM. It is the only control on the row for the commonest question a
 * reader of a list has -- "what does this one say?" -- and it needs no selection and no second
 * icon, because the chip is already there and already says what it is about. The list-level button
 * in the grid header is for the other question, "do these twelve agree?".
 *
 * THE PILLS OPEN THE THING THEY NAME, carrying the persona through the SSO handoff exactly as the
 * global inbox's cross-application links do — without it the reader arrives under a different
 * persona and a refusal reads as a broken link. The click is stopped from reaching the row, which
 * would otherwise navigate to the record instead.
 */
import React from "react";
import {Box, Chip, Tooltip} from "@mui/material";
import {AccountTreeOutlined, AttachFileOutlined, DescriptionOutlined} from "@mui/icons-material";

import AskAboutAttachments from "./AskAboutAttachments";
import {buildSsoHandoffUrl, handoffVisibility} from "../../util/ssoAuthenticate";

const SBOM_URL = process.env.REACT_APP_SBOM_URL || "https://sbom.cres-intelligence.io";

export default function ArtefactPills(
    {sbom, documents, attachments, revisionUSID, sessionContext}:
    {sbom: boolean; documents: boolean; attachments?: number;
     revisionUSID?: string | null; sessionContext?: any}) {

    const files = attachments ?? 0;
    // NOTHING AT ALL WHERE THERE IS NEITHER AND NO FILES. Most records in a quality system carry
    // none of the three, and a column that draws something on every row is one people stop reading.
    if (!sbom && !documents && files <= 0) return null;

    const openSbom = (event: React.MouseEvent) => {
        // THE ROW IS CLICKABLE TOO, and single-clicking it opens the record. Without this the
        // pill would open the composition AND navigate away behind it.
        event.stopPropagation();
        window.open(
            buildSsoHandoffUrl(`${SBOM_URL}/compositions/${revisionUSID}`,
                               sessionContext?.getAccessToken?.() ?? null,
                               handoffVisibility(sessionContext)),
            "_blank", "noopener");
    };

    return (
        // HEIGHT 100%, AND alignItems ALONE IS NOT ENOUGH. A cell renderer is dropped into a 48px
        // row, so a box that is only as tall as its own content centres its children against
        // ITSELF and then sits at the top of the row -- which is what put the Docs and SBOM chips
        // a few pixels high while every other column sat centred. Reported by Ed, 2026-10-21.
        //
        // ONE BOX, SO ONE FIX. Both chips live in here, which is why the SBOM pill had exactly the
        // same misalignment as the Docs one.
        //
        // The same treatment every other cell in ObjectRevisionTable already applies -- see
        // renderObjectNumberCell's `cell()` helper, which exists for this reason.
        <Box sx={{display: "flex", gap: "6px", alignItems: "center", height: "100%"}}>
            {files > 0 ? (
                // THE CHIP THAT SAYS THERE ARE FILES IS THE WAY TO ASK ABOUT THEM.
                //
                // A SECOND ICON WOULD HAVE BEEN THE OBVIOUS MOVE AND THE WRONG ONE. This chip is
                // already on the row, already says how many files there are, and already appears
                // only where there is something to read -- so it is the one affordance that needs
                // no teaching and costs no width. Adding a sparkle beside it would put two
                // controls on every row for one question.
                //
                // THE NUMBER STAYS THE LABEL. "4 files" is read at a glance down a column of forty
                // rows, where a superscript is not; the sparkle replaces the paperclip because the
                // chip now DOES something, and an icon that says so is worth more than one that
                // repeats the label.
                //
                // THIS RECORD ONLY. "What does this one say?" is the common question and wants no
                // selection at all; the header button is for "do these twelve agree?". Two
                // questions, two sizes, two controls.
                <AskAboutAttachments
                    scope={{kind: "Revision", objectRevisionUSID: String(revisionUSID ?? "")}}
                    scopeLabel={`Every file on this record (${files})`}
                >
                    {(open) => (
                        <Tooltip title={`Ask about the ${files} file${files === 1 ? "" : "s"} on `
                                        + `this record, including any answering a field on its form`}>
                            {/* A PAPERCLIP, NOT THE SPARKLE. This pill was wearing
                                AutoAwesomeOutlined -- the icon the ASSISTANT wears everywhere
                                else in this app: the nav bar, "Ask about their attachments", the
                                ask panel. The chip's job is to say THIS RECORD HAS FILES, and a
                                reader scanning a list for attachments was being shown the AI's
                                badge instead. That it also opens the assistant when clicked is
                                what the tooltip is for; the icon has to name the thing it
                                indicates. Reported by Ed, against both the "Has" column and the
                                card view -- which are one component, so this is one change. */}
                            <Chip size="small" variant="outlined" clickable
                                  icon={<AttachFileOutlined sx={{fontSize: 14}}/>}
                                  label={`${files} file${files === 1 ? "" : "s"}`}
                                  // THE ROW IS CLICKABLE TOO and opens the record. Without
                                  // stopping it the panel would open AND the page would navigate
                                  // out from under it -- the same reason the SBOM pill does this.
                                  onClick={(event: React.MouseEvent) => {
                                      event.stopPropagation();
                                      open(event as React.MouseEvent<HTMLElement>);
                                  }}
                                  sx={{fontSize: 11, height: 21, cursor: "pointer"}}/>
                        </Tooltip>
                    )}
                </AskAboutAttachments>
            ) : null}
            {documents ? (
                // NOT A LINK. The documents live on this record's own Parkour Documents tab, which
                // the row click already reaches; sending somebody to another application for
                // something one click away inside this one would be a worse journey.
                <Tooltip title="This revision has Parkour AI documents — see the Parkour AI Documents tab">
                    <Chip size="small" variant="outlined"
                          icon={<DescriptionOutlined sx={{fontSize: 14}}/>}
                          label="Docs"
                          sx={{fontSize: 11, height: 21, pointerEvents: "none"}}/>
                </Tooltip>
            ) : null}
            {sbom ? (
                <Tooltip title="This revision has a bill of materials — opens in the SBOM application">
                    <Chip size="small" variant="outlined" clickable
                          icon={<AccountTreeOutlined sx={{fontSize: 14}}/>}
                          label="SBOM"
                          onClick={openSbom}
                          sx={{fontSize: 11, height: 21, cursor: "pointer"}}/>
                </Tooltip>
            ) : null}
        </Box>
    );
}
